import { CommonModule } from "@angular/common";
import {
    booleanAttribute,
    Component,
    computed,
    inject,
    input,
    model,
    signal,
} from "@angular/core";
import { PreDocumentService } from "@malirang/api-client";
import { SegmentTabOption, SegmentTabsComponent } from "@malirang/app-share";
import { DocumentPreServiceType } from "@malirang/common";
import {
    Button,
    Input,
    ModalService,
    ScreenSizeService,
} from "@malirang/front-share";
import { ClickOutside } from "ngxtension/click-outside";
import { DocumentSelectTicket } from "../../../layout/document-select/ticket/document-select-ticket";
import { DocumentSelectTicketModal } from "../../../layout/document-select/ticket/document-select-ticket.modal";
import { DocumentStore } from "../../../store/document.store";
import {
    DocumentCardParams,
    PreDocumentCard,
} from "../pre-document-card/pre-document-card";
import { environment } from "../../../../../../env/env";

@Component({
    selector: "app-pre-document-list",
    templateUrl: "./pre-document-list.html",
    styleUrls: ["./pre-document-list.css"],
    imports: [
        ClickOutside,
        CommonModule,
        Input,
        Button,
        SegmentTabsComponent,
        DocumentSelectTicket,
        PreDocumentCard,
    ],
})
export class PreDocumentList {
    readonly documentStore = inject(DocumentStore);
    readonly preDocumentService = inject(PreDocumentService);
    readonly modalService = inject(ModalService);
    readonly screenSizeService = inject(ScreenSizeService);

    tabOptions: SegmentTabOption[] = [
        { label: "전체", value: "all" },
        { label: "필수", value: "required" },
        { label: "선택", value: "optional" },
    ];
    tab = model<string>("all");
    query = model<string>("");
    serviceOpen = signal<boolean>(false);
    onlyItems = input<boolean, string>(false, { transform: booleanAttribute });
    items = this.documentStore.preDocuments;

    isRequiredDocumentWrite = computed<boolean>(() => {
        const 기본상담지 = this.items().find(
            (item) => item.preServiceType === "기본상담지",
        );
        const 평가보고서 = this.items().find(
            (item) => item.preServiceType === "평가보고서",
        );

        return (
            기본상담지?.status === "COMPLETED" &&
            평가보고서?.status === "COMPLETED"
        );
    });

    list = computed<DocumentCardParams[]>(() => {
        const 기본상담지 = this.items()?.find(
            (item) => item?.preServiceType === "기본상담지",
        );
        const 심화상담지 = this.items()?.find(
            (item) => item?.preServiceType === "심화상담지",
        );
        const 평가보고서 = this.items()?.find(
            (item) => item?.preServiceType === "평가보고서",
        );
        const 발달재활서비스의뢰서 = this.items()?.find(
            (item) => item?.preServiceType === "발달재활서비스의뢰서",
        );
        const 아동청소년심리서비스의뢰서 = this.items()?.find(
            (item) => item?.preServiceType === "아동청소년심리서비스의뢰서",
        );

        const allItems: DocumentCardParams[] = [
            {
                required: true,
                name: DocumentPreServiceType.기본상담지,
                value: "기본상담지",
                updatedAt: 기본상담지?.updatedAt
                    ? new Date(기본상담지.updatedAt)
                    : undefined,
                status: 기본상담지?.status || "PENDING",
                phase: "PRE_SERVICE",
            },
            {
                required: false,
                name: DocumentPreServiceType.심화상담지,
                value: "심화상담지",
                status: 심화상담지?.status || "PENDING",
                updatedAt: 심화상담지?.updatedAt
                    ? new Date(심화상담지.updatedAt)
                    : undefined,
                phase: "PRE_SERVICE",
            },
            {
                required: true,
                name: DocumentPreServiceType.평가보고서,
                value: "평가보고서",
                status: 평가보고서?.status || "PENDING",
                updatedAt: 평가보고서?.updatedAt
                    ? new Date(평가보고서.updatedAt)
                    : undefined,
                phase: "PRE_SERVICE",
            },
            {
                required: false,
                name: DocumentPreServiceType.발달재활서비스의뢰서,
                value: "발달재활서비스의뢰서",
                status: 발달재활서비스의뢰서?.status || "PENDING",
                updatedAt: 발달재활서비스의뢰서?.updatedAt
                    ? new Date(발달재활서비스의뢰서.updatedAt)
                    : undefined,
                phase: "PRE_SERVICE",
                lock: false,
            },
            {
                required: false,
                name: DocumentPreServiceType.아동청소년심리서비스의뢰서,
                value: "아동청소년심리서비스의뢰서",
                status: 아동청소년심리서비스의뢰서?.status || "PENDING",
                updatedAt: 아동청소년심리서비스의뢰서?.updatedAt
                    ? new Date(아동청소년심리서비스의뢰서.updatedAt)
                    : undefined,
                phase: "PRE_SERVICE",
                lock: false,
            },
        ];

        // 탭 필터 적용
        let filtered = allItems;
        const currentTab = this.tab();
        if (currentTab === "required") {
            filtered = filtered.filter((item) => item.required === true);
        } else if (currentTab === "optional") {
            filtered = filtered.filter((item) => item.required === false);
        }

        // 검색어 필터 적용
        const searchQuery = this.query()?.trim().toLowerCase();
        if (searchQuery) {
            filtered = filtered.filter((item) =>
                item.name.toLowerCase().includes(searchQuery),
            );
        }

        return filtered;
    });

    handleToggleServiceOpen() {
        // 모바일/태블릿에서는 모달로 표시
        if (this.screenSizeService.isTabletOrMobile()) {
            this.modalService.create(DocumentSelectTicketModal);
            return;
        }

        this.serviceOpen.update((open) => !open);
    }
}
